import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import RouteSkeleton from "@/components/RouteSkeleton";
import IntegrationActivityLog from "../IntegrationActivityLog";
import {
  SectionCard,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  FormRow,
  FieldInput,
  Divider,
} from "@/components/ds";
import IconByName from "@/components/ds/IconByName";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SecureFormsConfig {
  configured: boolean;
  isEnabled: boolean;
  tenantSlug: string;
  hasApiKey: boolean;
  hasWebhookSecret: boolean;
  apiKeyHint: string | null;
  webhookCallbackUrl: string;
}

interface ResultBanner {
  success: boolean;
  message: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function generateWebhookSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — no-op
    }
  };
  return (
    <SecondaryButton onClick={handleCopy} aria-label="Copy to clipboard">
      {copied ? "Copied" : "Copy"}
    </SecondaryButton>
  );
}

function ResultBannerDisplay({ result }: { result: ResultBanner | null }) {
  if (!result) return null;
  return (
    <div
      className={`rounded-md p-3 text-sm ${
        result.success
          ? "bg-green-50 border border-green-200 text-green-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
      role="status"
    >
      {result.message}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function SecureFormsPanel() {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<SecureFormsConfig | null>(null);

  // Form fields
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState("");
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [tenantSlug, setTenantSlug] = useState("");
  const [isEnabled, setIsEnabled] = useState(false);

  // Action state
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saveResult, setSaveResult] = useState<ResultBanner | null>(null);
  const [testResult, setTestResult] = useState<ResultBanner | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.admin.get("SecureForms");
      const data: SecureFormsConfig = await resp.json();
      setConfig(data ?? null);
      setTenantSlug(data?.tenantSlug ?? "");
      setIsEnabled(data?.isEnabled ?? false);
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  async function handleSave() {
    setSaving(true);
    setSaveResult(null);
    setTestResult(null);
    try {
      const resp = await api.admin.post("SecureForms", {
        apiKey: apiKey.trim() || undefined,
        webhookSecret: webhookSecret.trim() || undefined,
        tenantSlug: tenantSlug.trim(),
        isEnabled,
      });
      const data = await resp.json();
      if (data?.success) {
        setSaveResult({ success: true, message: "Configuration saved." });
        setApiKey("");
        setWebhookSecret("");
        await loadConfig();
      } else {
        setSaveResult({ success: false, message: data?.error ?? "Failed to save." });
      }
    } catch {
      setSaveResult({ success: false, message: "Network error — check your connection and try again." });
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await api.admin.post("SecureForms/Test", {
        apiKey: apiKey.trim() || undefined,
      });
      const data = await resp.json();
      setTestResult({
        success: data?.success ?? false,
        message: data?.success
          ? data?.message ?? "Connected to SecureForms successfully."
          : data?.error ?? "Connection test failed.",
      });
    } catch {
      setTestResult({ success: false, message: "Network error — could not reach the server." });
    } finally {
      setTesting(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    setSaveResult(null);
    try {
      await api.admin.delete("SecureForms");
      setSaveResult({ success: true, message: "SecureForms integration removed." });
      setApiKey("");
      setWebhookSecret("");
      setTenantSlug("");
      setIsEnabled(false);
      await loadConfig();
    } catch {
      setSaveResult({ success: false, message: "Failed to remove configuration." });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <RouteSkeleton />;
  }

  const isConfigured = config?.configured ?? false;
  const callbackUrl = config?.webhookCallbackUrl ?? "";

  return (
    <div className="space-y-5 max-w-2xl">

      <SectionCard
        title="SecureForms"
        description="HIPAA-compliant secure form submissions with webhook-triggered Velaro workflows."
        action={
          <span
            className={`flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${
              isConfigured && isEnabled
                ? "bg-green-100 text-green-700"
                : "bg-zinc-100 text-zinc-500"
            }`}
          >
            {isConfigured && isEnabled ? "Connected" : isConfigured ? "Disabled" : "Not configured"}
          </span>
        }
      >
        <div className="rounded-md bg-blue-50 border border-blue-100 p-4 text-sm text-blue-800 space-y-1.5">
          <p className="font-medium">What SecureForms does:</p>
          <ul className="list-disc list-inside space-y-1 text-blue-700 text-xs">
            <li>Collect HIPAA-compliant intake forms, applications, and sensitive data</li>
            <li>Each submission fires a webhook to Velaro — your workflows respond automatically</li>
            <li>Supports conditional logic, file uploads, e-signatures, and encrypted field storage</li>
            <li>Velaro can pre-fill forms with visitor context and auto-route submissions to agents</li>
          </ul>
          <a
            href="https://velaro.com/secure-forms"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs mt-1 font-medium"
            aria-label="Learn more about SecureForms (opens in new tab)"
          >
            Learn more about SecureForms
            <svg viewBox="0 0 12 12" width="11" height="11" fill="none" aria-hidden="true">
              <path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </a>
        </div>
      </SectionCard>

      <SectionCard
        title="Configuration"
        action={
          <PrimaryButton onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </PrimaryButton>
        }
      >
        <div className="space-y-5">

          {isConfigured && (
            <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800 flex items-center gap-2">
              <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="7" fill="#16a34a" />
                <path d="M4 8l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>
                SecureForms is configured.
                {config?.apiKeyHint && (
                  <span className="ml-2 font-mono text-xs text-green-700">{config.apiKeyHint}</span>
                )}
              </span>
            </div>
          )}

          <FormRow
            label="Enable integration"
            hint="When disabled, webhook callbacks are ignored."
          >
            <button
              type="button"
              role="switch"
              aria-checked={isEnabled}
              aria-label={isEnabled ? "Disable SecureForms integration" : "Enable SecureForms integration"}
              onClick={() => setIsEnabled(!isEnabled)}
              className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${
                isEnabled ? "bg-[color:var(--color-primary)]" : "bg-zinc-300"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  isEnabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </FormRow>

          <FormRow
            label="Tenant slug"
            hint="Your SecureForms organization slug — found in your SecureForms dashboard URL."
          >
            <FieldInput
              id="sf-tenant-slug"
              value={tenantSlug}
              onChange={(e) => {
                setTenantSlug(e.target.value);
                setSaveResult(null);
              }}
              placeholder="acme-corp"
              className="font-mono"
            />
          </FormRow>

          <FormRow
            label={
              isConfigured && config?.hasApiKey
                ? "API key (leave blank to keep current key)"
                : "API key"
            }
            hint="Found in your SecureForms account under Settings → API Keys."
          >
            <div className="relative">
              <FieldInput
                id="sf-api-key"
                type={showApiKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  setSaveResult(null);
                  setTestResult(null);
                }}
                placeholder={
                  isConfigured && config?.hasApiKey
                    ? config?.apiKeyHint ?? "••••••••"
                    : "Enter your SecureForms API key"
                }
                autoComplete="new-password"
                className="pr-16 font-mono"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-500 hover:text-zinc-700 px-1.5 py-1"
                aria-label={showApiKey ? "Hide API key" : "Show API key"}
              >
                {showApiKey ? "Hide" : "Show"}
              </button>
            </div>
          </FormRow>

          <FormRow
            label={
              isConfigured && config?.hasWebhookSecret
                ? "Webhook secret (leave blank to keep current secret)"
                : "Webhook secret"
            }
            hint="Used by Velaro to verify inbound webhook signatures from SecureForms. Copy the same value into your SecureForms webhook settings."
          >
            <GhostButton
              onClick={() => {
                const secret = generateWebhookSecret();
                setWebhookSecret(secret);
                setShowWebhookSecret(true);
              }}
            >
              Generate
            </GhostButton>
            <div className="relative">
              <FieldInput
                id="sf-webhook-secret"
                type={showWebhookSecret ? "text" : "password"}
                value={webhookSecret}
                onChange={(e) => {
                  setWebhookSecret(e.target.value);
                  setSaveResult(null);
                }}
                placeholder={
                  isConfigured && config?.hasWebhookSecret
                    ? "••••••••••••••••"
                    : "Enter or generate a webhook secret"
                }
                autoComplete="new-password"
                className="pr-16 font-mono"
              />
              <button
                type="button"
                onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-500 hover:text-zinc-700 px-1.5 py-1"
                aria-label={showWebhookSecret ? "Hide webhook secret" : "Show webhook secret"}
              >
                {showWebhookSecret ? "Hide" : "Show"}
              </button>
            </div>
          </FormRow>

          <ResultBannerDisplay result={saveResult} />
          <ResultBannerDisplay result={testResult} />

          <Divider />

          <div className="flex items-center gap-3 flex-wrap">
            <SecondaryButton
              onClick={handleTest}
              disabled={testing || saving || (!apiKey && !isConfigured)}
            >
              {testing ? "Testing…" : "Test connection"}
            </SecondaryButton>

            {isConfigured && (
              <GhostButton onClick={handleDelete} disabled={saving} className="ml-auto text-red-600 hover:text-red-700">
                Remove integration
              </GhostButton>
            )}
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Webhook callback URL"
        description="Paste this URL into your SecureForms webhook settings so form submissions trigger Velaro workflows."
      >
        <FormRow
          label="Callback URL"
          hint="In SecureForms: Form Settings → Webhooks → Add Endpoint → paste this URL → set your webhook secret."
        >
          <div className="flex items-center gap-2">
            <FieldInput
              readOnly
              value={callbackUrl}
              aria-label="Webhook callback URL"
              className="flex-1 font-mono"
            />
            <CopyButton value={callbackUrl} />
          </div>
        </FormRow>
      </SectionCard>

      <SectionCard title="How it works">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {[
            { num: "1", label: "Visitor fills out a SecureForms form — encrypted, HIPAA-compliant", icon: "FileText" },
            { num: "2", label: "SecureForms sends a signed webhook to Velaro with the submission data", icon: "Zap" },
            { num: "3", label: "Velaro verifies the signature and triggers the matching workflow automatically", icon: "Repeat" },
          ].map((step, i) => (
            <React.Fragment key={step.num}>
              <div className="flex items-start gap-3 flex-1">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--color-primary)] text-white text-sm font-bold flex items-center justify-center">
                  {step.num}
                </span>
                <div>
                  <IconByName name={step.icon} className="w-4 h-4" />
                  <p className="text-sm text-zinc-700 mt-0.5">{step.label}</p>
                </div>
              </div>
              {i < 2 && (
                <svg
                  className="hidden sm:block flex-shrink-0 text-zinc-300"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M7 3l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                </svg>
              )}
            </React.Fragment>
          ))}
        </div>
      </SectionCard>

      <div className="mt-8">
        <h2 className="text-sm font-semibold text-zinc-700 mb-3">Activity log</h2>
        <IntegrationActivityLog integrationSlug="SecureForms" />
      </div>
    </div>
  );
}

export function BackToIntegrationLandingSecureForms() {
  return (
    <button
      type="button"
      onClick={() => {
        window.location.href = "/integrations";
      }}
      className="text-sm text-zinc-500 hover:text-zinc-700 flex items-center gap-1"
      aria-label="Back to integrations"
    >
      <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
        <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Integrations
    </button>
  );
}
